import { ChartDataPoint } from './types';

export type FilledChartDataPoint = ChartDataPoint & { isFilledPoint: boolean };

/**
 * Fills null values in chart data:
 * - Intermediate nulls: linearly interpolated between neighbors
 * - Leading nulls: backward-filled with first valid value
 * - Trailing nulls: forward-filled with last valid value
 * All filled points are marked for conditional rendering (e.g., hiding dots, showing "No data" in tooltip)
 */
export function fillNullValues(data: ChartDataPoint[]): FilledChartDataPoint[] {
	if (!data?.length) return [];

	const result: FilledChartDataPoint[] = data.map((p) => ({
		...p,
		originalValue: p.value,
		isFilledPoint: false,
	}));

	// Find if there's at least one valid value
	const hasValidValue = result.some((p) => p.value !== null && p.value !== undefined);

	// If no valid values found, return data as-is
	if (!hasValidValue) {
		return result;
	}

	// Fill all null values
	for (let i = 0; i < result.length; i++) {
		if (result[i].value !== null && result[i].value !== undefined) continue;

		// Find previous non-null
		let prevIdx = i - 1;
		while (prevIdx >= 0 && result[prevIdx].value === null) prevIdx--;

		// Find next non-null
		let nextIdx = i + 1;
		while (nextIdx < result.length && result[nextIdx].value === null) nextIdx++;

		let filledValue: number;

		if (prevIdx >= 0 && nextIdx < result.length) {
			// Intermediate null: linear interpolation
			const prevVal = result[prevIdx].value!;
			const nextVal = result[nextIdx].value!;
			const ratio = (i - prevIdx) / (nextIdx - prevIdx);
			filledValue = prevVal + (nextVal - prevVal) * ratio;
		} else if (prevIdx >= 0) {
			// Trailing null: forward-fill with last valid value
			filledValue = result[prevIdx].value!;
		} else if (nextIdx < result.length) {
			// Leading null: backward-fill with first valid value
			filledValue = result[nextIdx].value!;
		} else {
			// Should not reach here if hasValidValue is true
			continue;
		}

		result[i] = {
			...result[i],
			value: filledValue,
			isFilledPoint: true,
		};
	}

	return result;
}
